import { useEffect, useState, type ReactElement, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/useToast';

import { useDockerHubCredentials, useSetDockerHubCredentials } from '../api';

interface FieldErrors {
  username?: string;
  token?: string;
}

/**
 * Set/rotate the cluster-global DockerHub credentials (PTV2-1021 Phase 2). Shared by the
 * {@link DockerHubCredentialsDialog} and the standalone DockerHub console tab (PTV2-1383).
 *
 * Security: the token is write-only — GET returns only {@code tokenSet}/{@code updatedOn}, never the
 * secret, so the token field is a password input that always starts empty and is never pre-filled
 * from server state. The submitted token is not retained client-side beyond the PUT.
 *
 * @param active whether the credentials query should run (the dialog passes its {@code open} flag; the
 *     page passes {@code true})
 * @param onSaved optional callback fired after a successful save (the dialog closes; the page keeps the
 *     form mounted)
 * @param onCancel optional cancel handler — when provided a Cancel button is rendered (dialog only)
 */
export function DockerHubCredentialsForm({
  active,
  onSaved,
  onCancel,
}: {
  readonly active: boolean;
  readonly onSaved?: () => void;
  readonly onCancel?: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const credsQuery = useDockerHubCredentials(active);
  const setCreds = useSetDockerHubCredentials();

  const [username, setUsername] = useState('');
  const [token, setToken] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});

  // Seed the username from server state once it loads (never the token).
  useEffect(() => {
    const loaded = credsQuery.data;
    if (active && loaded != null) {
      setUsername((prev) => (prev.length === 0 ? loaded.username : prev));
    }
  }, [active, credsQuery.data]);

  function handleSubmit(event: SyntheticEvent): void {
    event.preventDefault();
    const u = username.trim();
    const nextErrors: FieldErrors = {};
    if (u.length === 0) nextErrors.username = t('routes.platform.tenants.dockerhub.usernameRequired');
    if (token.length === 0) nextErrors.token = t('routes.platform.tenants.dockerhub.tokenRequired');
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});
    setCreds.mutate(
      { username: u, token },
      {
        onSuccess: () => {
          setToken('');
          toast({ title: t('routes.platform.tenants.dockerhub.saved'), variant: 'success' });
          onSaved?.();
        },
        onError: (error) => {
          toast({
            title: t('routes.platform.tenants.dockerhub.error', { code: getErrorCodeForUi(error) }),
            variant: 'destructive',
          });
        },
      },
    );
  }

  const tokenSet = credsQuery.data?.tokenSet === true;

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div className="flex items-center gap-2">
        <Badge variant={tokenSet ? 'secondary' : 'outline'}>
          {tokenSet
            ? t('routes.platform.tenants.dockerhub.tokenSet')
            : t('routes.platform.tenants.dockerhub.tokenUnset')}
        </Badge>
        {credsQuery.data?.updatedOn != null && (
          <span className="text-xs text-muted-foreground">
            {t('routes.platform.tenants.dockerhub.updatedOn', { date: credsQuery.data.updatedOn })}
          </span>
        )}
      </div>
      <FormField id="dockerhub-username" label={t('routes.platform.tenants.dockerhub.username')} error={errors.username}>
        {(fp) => (
          <Input {...fp} value={username} onChange={(e) => { setUsername(e.target.value); }} autoComplete="off" />
        )}
      </FormField>
      <FormField id="dockerhub-token" label={t('routes.platform.tenants.dockerhub.token')} error={errors.token}>
        {(fp) => (
          <Input
            {...fp}
            type="password"
            value={token}
            onChange={(e) => { setToken(e.target.value); }}
            placeholder={t('routes.platform.tenants.dockerhub.tokenPlaceholder')}
            autoComplete="new-password"
          />
        )}
      </FormField>
      <p className="-mt-2 text-xs text-muted-foreground">
        {t('routes.platform.tenants.dockerhub.tokenHint')}
      </p>
      <div className="flex justify-end gap-2">
        {onCancel !== undefined && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={setCreds.isPending}>
            {t('routes.platform.tenants.dockerhub.cancel')}
          </Button>
        )}
        <Button type="submit" loading={setCreds.isPending}>
          {t('routes.platform.tenants.dockerhub.save')}
        </Button>
      </div>
    </form>
  );
}
